<script>
  import { isAuthenticated, signOut, user } from '../stores/auth.js';
  import Logo from './Logo.svelte';

  let mobileMenuOpen = false;
  let scrolled = false;

  function handleScroll() {
    scrolled = window.scrollY > 20;
  }

  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
  }

  function closeMobileMenu() {
    mobileMenuOpen = false;
  }

  function handleLogoClick() {
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Get user initials for avatar
  $: userInitials = $user?.displayName
    ? $user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : ($user?.email?.[0] || 'U').toUpperCase();
</script>

<svelte:window on:scroll={handleScroll} />

<nav class="fixed top-0 left-0 right-0 z-[100] transition-all duration-300
  {scrolled ? 'bg-white border-b border-slate-200 shadow-sm' : 'bg-white/0'}">
  <div class="container-wide">
    <div class="flex items-center justify-between h-16 lg:h-20">
      <!-- Logo -->
      <a href="/" on:click={handleLogoClick} class="focus-ring rounded-lg">
        <Logo />
      </a>

      <!-- Desktop Navigation - Only show for non-authenticated users -->
      {#if !$isAuthenticated}
        <div class="hidden lg:flex items-center gap-8">
          <a href="/how-it-works" class="text-slate-600 hover:text-slate-900 font-medium transition-colors">How It Works</a>
          <a href="/features" class="text-slate-600 hover:text-slate-900 font-medium transition-colors">Features</a>
          <a href="/pricing" class="text-slate-600 hover:text-slate-900 font-medium transition-colors">Pricing</a>
          <a href="/case-studies" class="text-slate-600 hover:text-slate-900 font-medium transition-colors">Case Studies</a>
          <a href="/help" class="text-slate-600 hover:text-slate-900 font-medium transition-colors">Help</a>
        </div>
      {/if}

      <!-- Auth Buttons -->
      <div class="hidden lg:flex items-center gap-2">
        {#if $isAuthenticated}
          <a href="/dashboard" class="btn-ghost text-sm">Dashboard</a>
          <a href="/billing" class="btn-ghost text-sm">Billing</a>
          <a href="/plans" class="btn-ghost text-sm">Plans</a>
          <a href="/settings" class="btn-ghost text-sm">Settings</a>

          <!-- User Info -->
          <div class="flex items-center gap-3 ml-2 pl-3 border-l border-slate-200">
            <!-- Avatar -->
            {#if $user?.photoURL}
              <img src={$user.photoURL} alt="Avatar" class="w-8 h-8 rounded-full object-cover ring-2 ring-brand-100" />
            {:else}
              <div class="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                {userInitials}
              </div>
            {/if}

            <!-- Display Name or Email -->
            <span class="text-sm text-slate-600 max-w-[150px] xl:max-w-[200px] truncate hidden xl:inline" title={$user?.email || ''}>
              {$user?.displayName || $user?.email || 'User'}
            </span>
          </div>

          <button on:click={signOut} class="btn-secondary btn-sm">Sign Out</button>
        {:else}
          <a href="/login" class="btn-ghost">Sign In</a>
          <a href="/signup" class="btn-primary">Get Started</a>
        {/if}
      </div>

      <!-- Mobile Menu Button -->
      <button
        on:click={toggleMobileMenu}
        class="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors focus-ring"
        aria-label="Toggle menu"
      >
        {#if mobileMenuOpen}
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        {:else}
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        {/if}
      </button>
    </div>
  </div>

  <!-- Mobile Menu -->
  {#if mobileMenuOpen}
    <div class="lg:hidden bg-white border-t border-slate-200 shadow-lg animate-slide-down">
      <div class="container-wide py-4 space-y-1">
        <!-- Show marketing links only for non-authenticated -->
        {#if !$isAuthenticated}
          <a href="/how-it-works" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">How It Works</a>
          <a href="/features" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Features</a>
          <a href="/industries" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Industries</a>
          <a href="/pricing" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Pricing</a>
          <a href="/case-studies" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Case Studies</a>
          <a href="/help" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Help</a>
          <a href="/faq" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">FAQ</a>
        {/if}

        <div class="{$isAuthenticated ? '' : 'pt-4 mt-4 border-t border-slate-100'} space-y-2">
          {#if $isAuthenticated}
            <!-- User Info in Mobile Menu -->
            <div class="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl mb-3">
              <!-- Avatar -->
              {#if $user?.photoURL}
                <img src={$user.photoURL} alt="Avatar" class="w-10 h-10 rounded-full object-cover ring-2 ring-brand-100" />
              {:else}
                <div class="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                  {userInitials}
                </div>
              {/if}

              <!-- User Details -->
              <div class="flex-1 min-w-0">
                {#if $user?.displayName}
                  <p class="font-medium text-slate-900 truncate">{$user.displayName}</p>
                {/if}
                <p class="text-sm text-slate-600 truncate">{$user?.email || 'User'}</p>
              </div>
            </div>

            <a href="/dashboard" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Dashboard</a>
            <a href="/billing" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Billing</a>
            <a href="/plans" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Plans</a>
            <a href="/settings" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Settings</a>
            <button
              on:click={() => { signOut(); closeMobileMenu(); }}
              class="w-full btn-secondary"
            >
              Sign Out
            </button>
          {:else}
            <a href="/login" on:click={closeMobileMenu} class="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl font-medium transition-colors">Sign In</a>
            <a href="/signup" on:click={closeMobileMenu} class="block w-full btn-primary text-center">
              Get Started
            </a>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</nav>
